import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

export interface BreadcrumbItem {
    label: string;
    translateKey: string;
    route: string;
    isActive: boolean;
}

@Injectable({
    providedIn: "root",
})
export class SettingsBreadcrumbService {
    private breadcrumbsSubject = new BehaviorSubject<BreadcrumbItem[]>([]);
    public breadcrumbs$: Observable<BreadcrumbItem[]> = this.breadcrumbsSubject.asObservable();

    private settingsPages = new Map<string, { translateKey: string; route: string }>([
        ["main", { translateKey: "Settings.TrackingSettings", route: "/settings/main" }],
        ["general", { translateKey: "Settings.Menu.Title.General", route: "/settings/general" }],
        ["mappingofstatuses", { translateKey: "Settings.Menu.Title.MappingOfStatuses", route: "/settings/mappingofstatuses" }],
        ["dashboardmanagement", { translateKey: "Settings.Menu.Title.DashboardManagement", route: "/settings/dashboardmanagement" }],
        ["audittrail", { translateKey: "Settings.Menu.Title.AuditTrail", route: "/settings/audittrail" }],
        ["categories", { translateKey: "Settings.Menu.Title.Categories", route: "/settings/categories" }],
    ]);

    constructor() {}

    /**
     * Set breadcrumbs for a settings page
     * @param currentPageKey - The key of the current page (e.g., 'general', 'mapping-of-statuses')
     */
    setBreadcrumbs(currentPageKey: string): void {
        const breadcrumbs: BreadcrumbItem[] = [];

        // Always add "Tracking Settings" as the first breadcrumb
        const landingPage = this.settingsPages.get("main");
        if (landingPage) {
            breadcrumbs.push({
                label: "Tracking Settings",
                translateKey: landingPage.translateKey,
                route: landingPage.route,
                isActive: currentPageKey === "main",
            });
        }

        // Add current page as second breadcrumb if it's not the landing page
        if (currentPageKey !== "main" && currentPageKey !== "landing") {
            const currentPage = this.settingsPages.get(currentPageKey);
            if (currentPage) {
                breadcrumbs.push({
                    label: currentPage.translateKey,
                    translateKey: currentPage.translateKey,
                    route: currentPage.route,
                    isActive: true,
                });
            }
        }

        this.breadcrumbsSubject.next(breadcrumbs);
    }

    /**
     * Set custom breadcrumbs (useful for dynamic pages like user edit)
     * @param breadcrumbs - Array of breadcrumb items
     */
    setCustomBreadcrumbs(breadcrumbs: BreadcrumbItem[]): void {
        this.breadcrumbsSubject.next(breadcrumbs);
    }

    /**
     * Clear breadcrumbs
     */
    clearBreadcrumbs(): void {
        this.breadcrumbsSubject.next([]);
    }

    /**
     * Get the configured route for a settings page
     * @param pageKey - The key of the page
     * @returns The route string or undefined if not found
     */
    getPageRoute(pageKey: string): string | undefined {
        return this.settingsPages.get(pageKey)?.route;
    }
}
